import { useContext, useEffect, useState } from 'preact/hooks';
import { SessionContext } from '../app.jsx';
import { get, put } from '../api.js';
import { Link, navigate } from '../router.js';
import { fmtRange } from '../../lib/dates.js';

const OFFLINE = 'Nema signala — opremu upisuješ dok imaš signal.';
const shortText = (it) => `Slobodno ${it.free} od ${it.total} — smanji količinu ili izaberi drugu opremu.`;

// SC-003 Upis opreme: the items the guide takes on this tour, each against its free count for the tour's dates.
export function EquipmentEntry({ params }) {
  const id = Number(params.id);
  const { tick } = useContext(SessionContext);
  const [dep, setDep] = useState(null); // GET /departures/:id
  const [items, setItems] = useState(null); // [{ id, name, total, free, mine }]
  const [rows, setRows] = useState([]); // [{ itemId, qty }] with qty as typed
  const [picking, setPicking] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== 'undefined' && navigator.onLine === false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Opened with no signal: only the notice; the app's online refresh (tick) loads it again. A loaded form is kept.
  useEffect(() => {
    if (dep && items && !offline) return;
    if (navigator.onLine === false) return setOffline(true);
    Promise.all([get(`/departures/${id}`), get(`/departures/${id}/equipment`)]).then(([d, eq]) => {
      if (d.offline || eq.offline) return setOffline(true);
      setOffline(false);
      setDep(d);
      setItems(eq.items);
      setRows(eq.items.filter((i) => i.mine > 0).map((i) => ({ itemId: i.id, qty: String(i.mine) })));
    }, (e) => (e.status === 0 ? setOffline(true) : setError(e.message)));
  }, [id, tick]);

  if (offline) return <p class="alert">{OFFLINE}</p>;
  if (!dep || !items) return error ? <p class="alert" role="alert">{error}</p> : null;

  const { departure: d, mine, released } = dep;
  const head = (
    <>
      <h1>Upiši opremu</h1>
      <p class="sub">{`${d.tour.name}, ${fmtRange(d.start_date, d.end_date)}`}</p>
    </>
  );
  if (!mine) {
    return (
      <>
        {head}
        <p class="empty">{released ? 'Popis je sačuvan.' : 'Prvo se prijavi na ovu turu.'} <Link href={`/ture/${id}`}>Na turu</Link></p>
      </>
    );
  }
  if (items.length === 0) return <>{head}<p class="empty">Kancelarija još nije unela opremu.</p></>;

  const byId = new Map(items.map((i) => [i.id, i]));
  const isShort = (r) => Number(r.qty) > byId.get(r.itemId).free;
  const anyShort = rows.some(isShort);
  const others = items.filter((i) => !rows.some((r) => r.itemId === i.id));
  const setQty = (itemId, qty) => setRows((rs) => rs.map((r) => (r.itemId === itemId ? { ...r, qty } : r)));

  const pick = (e) => {
    const itemId = Number(e.currentTarget.value);
    if (!itemId) return;
    setRows((rs) => [...rs, { itemId, qty: '1' }]);
    setPicking(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await put(`/departures/${id}/equipment`, { items: rows.map((r) => ({ itemId: r.itemId, qty: r.qty.trim() })) });
      navigate(`/ture/${id}`);
      return;
    } catch (err) {
      const short = err.details?.short;
      if (short) {
        // the server's free counts are newer: the warnings follow them
        setItems((its) => its.map((i) => {
          const s = short.find((x) => x.itemId === i.id);
          return s ? { ...i, free: s.free, total: s.total } : i;
        }));
      } else {
        setError(err.status === 0 ? OFFLINE : err.message); // no signal: the form and the typed quantities stay
      }
    }
    setBusy(false);
  };

  return (
    <>
      {head}
      <form class="entry-form" onSubmit={save}>
        {rows.length === 0 && <p class="empty">Još nisi izabrao opremu. Dodaj je ispod.</p>}
        {rows.length > 0 && (
          <ul class="plain-list rows">
            {rows.map((r) => {
              const it = byId.get(r.itemId);
              return (
                <li key={r.itemId} class="take-row">
                  <div class="take-main">
                    <span class="take-name">{it.name}</span>
                    <input class="qty" type="number" inputmode="numeric" min="1" max="10000" step="1" required aria-label={it.name}
                      value={r.qty} onInput={(e) => setQty(r.itemId, e.currentTarget.value)} />
                    <button type="button" class="btn-quiet" aria-label={`Ukloni ${it.name}`}
                      onClick={() => setRows((rs) => rs.filter((x) => x.itemId !== r.itemId))}>Ukloni</button>
                  </div>
                  <p class="take-free num">{`slobodno ${it.free} od ${it.total}`}</p>
                  {isShort(r) && <p class="alert" role="alert">{shortText(it)}</p>}
                </li>
              );
            })}
          </ul>
        )}

        {others.length > 0 && (picking ? (
          <label class="field pick">
            <span>Izaberi stavku</span>
            <select onChange={pick} value="">
              <option value="" disabled>Izaberi…</option>
              {others.map((i) => <option key={i.id} value={String(i.id)}>{`${i.name} (slobodno ${i.free})`}</option>)}
            </select>
          </label>
        ) : <button type="button" class="btn-quiet" onClick={() => setPicking(true)}>Dodaj opremu</button>)}

        {error && <p class="alert" role="alert">{error}</p>}
        <div class="actions">
          <button class="btn" disabled={busy || anyShort}>Sačuvaj</button>
        </div>
      </form>
    </>
  );
}
